import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import type { Publication } from '~/interfaces/publication';
import { useGroupSubscriptionsModals } from '~/routes/subscribe/$subscriberId/_components/v2/GroupSubscriptions/useGroupSubscriptionsModals';
import { useRestoreSubscription } from '~/routes/subscribe/$subscriberId/_components/v2/GroupSubscriptions/useRestoreSubscription';
import useGroupSubscriptions from '~/routes/subscribe/$subscriberId/_components/v2/hooks/useGroupSubscriptions';
import toast from '~/utils/toast';

import { GroupSubscriptionsContext, type GroupSubscriptionsContextType } from './GroupSubscriptionsContext';
import { useTabStateContext } from './useTabStateContext';

type Props = {
  publication: Publication;
  subscriberId: string;
  isLoggedIn: boolean;
  children: React.ReactNode;
};

/**
 * Owns the group-subscriptions domain — data + bulk mutators, modal coordination, the
 * mutation-version counter + seat-addition watcher, the shared translations dict, and the
 * failed-payment-retry / restore / change-plan handlers. Consumes `TabStateProvider` to resolve a
 * card's "view billing" to the Billing tab. Renders no UI; the colocated
 * `GroupSubscriptionsSection` child renders the Groups tab. The scenario-applied
 * `effectiveGroupSubscriptions` (used by the General tab's overlap detection) is owned
 * separately by `EffectiveGroupSubscriptionsProvider`, which fetches its own lightweight
 * read of the groups rather than coupling to this groups-tab provider.
 */
export const GroupSubscriptionsProvider = ({ publication, subscriberId, isLoggedIn, children }: Props) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { setActiveTab } = useTabStateContext();

  // BE-resolved Flipper flag (BEE-18968). Default `false` so a missing field
  // (older Swarm response) keeps the tab off rather than leaking it to every
  // publication.
  const groupSubscriptionsEnabled = publication.group_subscriptions_enabled ?? false;

  // Raw resource lookup preserves the literal `{{count}}` placeholder so the
  // dream-component can interpolate per-group seat counts. `t()` would either
  // resolve the placeholder to "0" or pick the singular variant.
  const rawTranslation = useCallback(
    (key: string, fallback: string) => {
      const value = i18n.getResource(i18n.language, 'common', key);
      return typeof value === 'string' ? value : fallback;
    },
    [i18n],
  );

  // Past-due "Update payment method" posts here; the action mints a fresh Stripe
  // portal session and redirects. `return_to` is the current page so the error
  // path (and the dev stub) can come back here — explicit, not Referer-derived.
  const groupBillingPortalAction = useMemo(
    () => `/group_subscription_billing_portal?return_to=${encodeURIComponent(location.pathname + location.search)}`,
    [location.pathname, location.search],
  );

  const {
    data: groupSubscriptions,
    error: groupSubscriptionsError,
    refetch: refetchGroupSubscriptions,
    isLoading: isLoadingGroupSubscriptions,
    bulkRemoveSeats: bulkRemoveGroupSubscriptionSeats,
    bulkCancelInvites: bulkCancelGroupSubscriptionInvites,
    bulkResendInvitations: bulkResendGroupSubscriptionInvitations,
    createInvitations: createGroupSubscriptionInvitations,
    validateSeats: validateGroupSubscriptionSeats,
  } = useGroupSubscriptions({
    // `enabled: false` prevents calling an endpoint the publication doesn't
    // have permission to hit (Flipper flag off).
    enabled: isLoggedIn && groupSubscriptionsEnabled,
  });

  // Shared dict for `GroupSubscriptionsElement` AND the modal layer. Kept here
  // (not inline below) so the invite modals can resolve `card.manage_seats`
  // for their inline "Manage seats" link instead of falling back to English.
  const groupSubscriptionsTranslations = useMemo(
    () => ({
      'group_subscriptions.empty_state': t('manage.groups.empty_state'),
      'group_subscriptions.empty_state_title': t('manage.groups.empty_state_title'),
      'group_subscriptions.empty_state_description': t('manage.groups.empty_state_description'),
      'group_subscriptions.no_seats.heading_one': rawTranslation(
        'manage.groups.no_seats.heading_one',
        '{{count}} seat available',
      ),
      'group_subscriptions.no_seats.heading_other': rawTranslation(
        'manage.groups.no_seats.heading_other',
        '{{count}} seats available',
      ),
      'group_subscriptions.no_seats.description': t('manage.groups.no_seats.description'),
      'group_subscriptions.no_seats.cta': t('manage.groups.no_seats.cta'),
      'group_subscriptions.card.plan_suffix': t('manage.groups.card.plan_suffix'),
      'group_subscriptions.card.billed_annually': t('manage.groups.card.billed_annually'),
      'group_subscriptions.card.per_seat_per_unit': t('manage.groups.card.per_seat_per_unit'),
      'group_subscriptions.card.total_per_year': t('manage.groups.card.total_per_year'),
      'group_subscriptions.card.total': t('manage.groups.card.total'),
      'group_subscriptions.card.seats_count': t('manage.groups.card.seats_count'),
      'group_subscriptions.card.renews_on': t('manage.groups.card.renews_on'),
      'group_subscriptions.card.ends_on': t('manage.groups.card.ends_on'),
      'group_subscriptions.card.expand_aria': t('manage.groups.card.expand_aria'),
      'group_subscriptions.card.collapse_aria': t('manage.groups.card.collapse_aria'),
      'group_subscriptions.card.manage_plan': t('manage.groups.card.manage_plan'),
      'group_subscriptions.card.manage_seats': t('manage.groups.card.manage_seats'),
      'group_subscriptions.card.contact_publisher': t('manage.groups.card.contact_publisher'),
      'group_subscriptions.card.billed_by_publisher_title': t('manage.groups.card.billed_by_publisher_title'),
      'group_subscriptions.card.billed_by_publisher_body': rawTranslation(
        'manage.groups.card.billed_by_publisher_body',
        'Billing is managed separately. Contact {{email}} with any billing-related questions.',
      ),
      'group_subscriptions.card.billed_by_publisher_body_no_email': t(
        'manage.groups.card.billed_by_publisher_body_no_email',
      ),
      'group_subscriptions.card.added_label': t('manage.groups.card.added_label'),
      'group_subscriptions.card.billing_details': t('manage.groups.card.billing_details'),
      'group_subscriptions.card.cancel_subscription': t('manage.groups.card.cancel_subscription'),
      'group_subscriptions.card.keep_subscription': t('manage.groups.card.keep_subscription'),
      'group_subscriptions.card.invite': t('manage.groups.card.invite'),
      'group_subscriptions.card.invite_by_email': t('manage.groups.card.invite_by_email'),
      'group_subscriptions.card.bulk_invite_csv': t('manage.groups.card.bulk_invite_csv'),
      'group_subscriptions.card.canceled_badge': t('manage.groups.card.canceled_badge'),
      'group_subscriptions.card.scheduled.title': t('manage.groups.card.scheduled.title'),
      'group_subscriptions.card.scheduled.body': rawTranslation(
        'manage.groups.card.scheduled.body',
        'This group subscription starts on {{date}}. You can invite members once it begins.',
      ),
      'group_subscriptions.card.scheduled.body_no_date': t('manage.groups.card.scheduled.body_no_date'),
    }),
    [t, rawTranslation],
  );

  // Bumped on mutation success — cards watch this to refetch their seats slice.
  const [groupSubscriptionsMutationVersion, setGroupSubscriptionsMutationVersion] = useState(0);
  const handleGroupSubscriptionMutationSuccess = useCallback(() => {
    refetchGroupSubscriptions();
    setGroupSubscriptionsMutationVersion((v) => v + 1);
  }, [refetchGroupSubscriptions]);

  // While a seat addition is pending, useGroupSubscriptions fast-polls the index,
  // so the banner + header counts refresh on their own. The expanded seats table
  // refetches off mutationVersion, though — so when pending resolves (webhook
  // landed: capacity bumped + queue invited, atomically), bump it once to surface
  // the freshly-invited rows without a manual refresh.
  const anySeatAdditionPending = groupSubscriptions.some((g) => g.pending_seat_addition != null);
  const wasSeatAdditionPending = useRef(anySeatAdditionPending);
  useEffect(() => {
    if (wasSeatAdditionPending.current && !anySeatAdditionPending) {
      setGroupSubscriptionsMutationVersion((v) => v + 1);
    }
    wasSeatAdditionPending.current = anySeatAdditionPending;
  }, [anySeatAdditionPending]);

  const modals = useGroupSubscriptionsModals(groupSubscriptions, {
    onBulkRemoveSeats: bulkRemoveGroupSubscriptionSeats,
    onBulkCancelInvites: bulkCancelGroupSubscriptionInvites,
    onBulkResendInvitations: bulkResendGroupSubscriptionInvitations,
    onCreateInvitations: createGroupSubscriptionInvitations,
    onValidateSeats: validateGroupSubscriptionSeats,
    onMutationSuccess: handleGroupSubscriptionMutationSuccess,
    translations: groupSubscriptionsTranslations,
  });

  // `Stripe::CreateSession` → `GroupSubscriptions::CreatePending` resurrects
  // the payment_failed row by tuple, so a fresh Checkout is the retry path.
  const handleRetryFailedPayment = useCallback(
    async (groupId: string) => {
      const group = groupSubscriptions.find((g) => g.id === groupId);
      if (!group) return;
      try {
        const params = new URLSearchParams({
          price_id: group.price_id,
          group_purchase_quantity: String(group.max_seats),
          group_name: group.group_name,
          email: group.purchaser_email,
          nectar_upgrade_url: window.location.href,
        });
        const response = await fetch(`/stripe_session?${params}`);
        const raw: unknown = await response.json();
        if (raw && typeof raw === 'object' && 'url' in raw && typeof raw.url === 'string') {
          window.location.assign(raw.url);
          return;
        }
        toast.error(t('manage.groups.retry.error'));
      } catch {
        toast.error(t('manage.groups.retry.error'));
      }
    },
    [groupSubscriptions, t],
  );

  const { restoreSubscription } = useRestoreSubscription();
  const handleRestoreSubscription = useCallback(
    async (groupId: string) => {
      const result = await restoreSubscription(groupId);
      if (result.ok) {
        toast.success(t('manage.groups.restore.success'));
        handleGroupSubscriptionMutationSuccess();
      } else {
        toast.error(result.error ?? t('manage.groups.restore.error'));
      }
    },
    [restoreSubscription, handleGroupSubscriptionMutationSuccess, t],
  );

  // Surface group-subs fetch failures as a toast rather than a tab. We can't
  // know if the subscriber has group subs when the fetch fails, so showing
  // the tab would imply they do. Toast keeps them informed without lying
  // about state.
  useEffect(() => {
    if (groupSubscriptionsError) {
      toast.error(groupSubscriptionsError);
    }
  }, [groupSubscriptionsError]);

  const handleViewBilling = useCallback(() => setActiveTab('billing'), [setActiveTab]);

  const value = useMemo<GroupSubscriptionsContextType>(
    () => ({
      groupSubscriptions,
      isLoadingGroupSubscriptions,
      groupSubscriptionsMutationVersion,
      translations: groupSubscriptionsTranslations,
      groupBillingPortalAction,
      modals,
      onRetryFailedPayment: handleRetryFailedPayment,
      onRestoreSubscription: handleRestoreSubscription,
      onViewBilling: handleViewBilling,
    }),
    [
      groupSubscriptions,
      isLoadingGroupSubscriptions,
      groupSubscriptionsMutationVersion,
      groupSubscriptionsTranslations,
      groupBillingPortalAction,
      modals,
      handleRetryFailedPayment,
      handleRestoreSubscription,
      handleViewBilling,
    ],
  );

  return <GroupSubscriptionsContext.Provider value={value}>{children}</GroupSubscriptionsContext.Provider>;
};
